/**
 * Feature Flags Redux Slice
 *
 * Manages feature availability state fetched from backend status endpoints.
 * Used to conditionally show/hide UI elements based on backend configuration.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '@store/index';
import { api } from '../../services/api';

interface FeatureFlagsState {
  cadEnabled: boolean | null;
  cadThicknessEnabled: boolean | null;
  /** `null` until the status call resolves — see the loading convention below. */
  designVerificationEnabled: boolean | null;
  loading: boolean;
}

const initialState: FeatureFlagsState = {
  cadEnabled: null,
  cadThicknessEnabled: null,
  designVerificationEnabled: null,
  loading: false,
};

export const fetchCadStatus = createAsyncThunk(
  'featureFlags/fetchCadStatus',
  async () => {
    const data = await api.get<{
      enabled: boolean;
      thickness_enabled?: boolean;
    }>('/cad/status');
    return {
      cadEnabled: data.enabled,
      cadThicknessEnabled: data.thickness_enabled === true,
    };
  },
);

export const fetchVerificationStatus = createAsyncThunk(
  'featureFlags/fetchVerificationStatus',
  async () => {
    const data = await api.get<{ enabled: boolean }>('/verification/status');
    return data.enabled === true;
  },
);

const featureFlagsSlice = createSlice({
  name: 'featureFlags',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCadStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCadStatus.fulfilled, (state, action) => {
        state.cadEnabled = action.payload.cadEnabled;
        state.cadThicknessEnabled = action.payload.cadThicknessEnabled;
        state.loading = false;
      })
      .addCase(fetchCadStatus.rejected, (state) => {
        state.cadEnabled = false;
        state.cadThicknessEnabled = false;
        state.loading = false;
      })
      .addCase(fetchVerificationStatus.fulfilled, (state, action) => {
        state.designVerificationEnabled = action.payload;
      })
      // Fail closed: an unanswered status call must not leave it on display.
      .addCase(fetchVerificationStatus.rejected, (state) => {
        state.designVerificationEnabled = false;
      });
  },
});

export const selectCadEnabled = (state: RootState) => state.featureFlags.cadEnabled;
export const selectCadThicknessEnabled = (state: RootState) =>
  state.featureFlags.cadThicknessEnabled;
export const selectDesignVerificationEnabled = (state: RootState) =>
  state.featureFlags.designVerificationEnabled;

export default featureFlagsSlice.reducer;
